import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { PopupMessages } from "src/app/backend/popup-message";
import { RequestListItem } from "src/app/models/requests/request-list-item";
import { Service } from "src/app/models/services/service";
import { PortalAnimation } from "src/app/portal-animation";
import { LoadingService } from "src/app/services/loading/loading.service";
import { PopupMessageService } from "src/app/services/popup-message/popup-message.service";
import { RequestService } from "src/app/services/request/request.service";

@Component({
  selector: "app-urgent-request-page",
  templateUrl: "./urgent-request-page.component.html",
  styleUrls: ["./urgent-request-page.component.scss"],
  animations: [PortalAnimation.DropDown],
})
export class UrgentRequestPageComponent implements OnInit {
  requests: RequestListItem[] = [];
  caches: RequestListItem[] = [];
  count: number = 0;
  pages: number = 0;

  filters: any = {
    page: 1,
    itemsPerPage: 20,
    searchInput: null,
    services: [],
    districts: [],
    subDistricts: [],
    from: null,
    to: null,
    statuses: [],
    isDeleted: null,
    isUrgent: null,
    idOrder: null,
    dateOrder: true,
  };

  constructor(
    private requestService: RequestService,
    private loadingService: LoadingService,
    private route: ActivatedRoute,
    private router: Router,
    private popupMessageService: PopupMessageService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((value) => {
      this.filters.page = parseInt(value.page) || 1;
      this.filters.itemsPerPage = parseInt(value.itemsPerPage) || 20;
      this.fetch();
    });
  }

  fetch(): void {
    this.loadingService.startLoading();
    this.requestService
      .getRequests(this.getQueryString())
      .pipe(finalize(() => this.loadingService.stopLoading()))
      .subscribe((value) => {
        this.requests = value.list;
        this.count = value.count;
        this.pages =
          value.count != 0
            ? Math.ceil(value.count / this.filters.itemsPerPage)
            : 0;
        this.requests.forEach((request) => {
          if (this.caches.find((r) => r.uuId == request.uuId))
            request.selected = true;
        });
      });
  }

  searchFilterAction: (input: string) => void = ((input: string) => {
    this.filters.searchInput = input;
    this.filters.page = 1;
    this.fetch();
  }).bind(this);

  addressFilterAction = ((value: string[], subDistricts: string[] | any) => {
    this.filters.districts = value;
    this.filters.subDistricts = subDistricts;
    this.filters.page = 1;
    this.fetch();
  }).bind(this);

  serviceFilterAction = ((value: Service[] | any) => {
    this.filters.services = value;
    this.filters.page = 1;
    this.fetch();
  }).bind(this);

  dateFilterAction = ((start: Date, end: Date) => {
    this.filters.from = start;
    this.filters.to = end;
    this.filters.page = 1;
    this.fetch();
  }).bind(this);

  statusFilterAction = ((statuses: string[], urgent: boolean) => {
    this.filters.statuses = statuses;
    this.filters.isUrgent = urgent;
    this.filters.page = 1;
    this.fetch();
  }).bind(this);

  changePage(): void {
    this.router.navigate(["/main/activity/overview/request"], {
      queryParams: {
        page: this.filters.page,
        itemsPerPage: this.filters.itemsPerPage,
      },
    });
  }

  deleteRequest(uuId: string): void {
    var subscriber = this.popupMessageService.executeSuccessSignal.subscribe(
      (value) => {
        this.fetch();
        subscriber.unsubscribe();
      },
    );

    this.popupMessageService.messageSignal.emit(
      PopupMessages.DeleteRequestMessage(
        this.requestService.deleteRequests([uuId]),
      ),
    );
  }

  deleteRequestList(): void {
    var subscriber = this.popupMessageService.executeSuccessSignal.subscribe(
      (value) => {
        this.caches = [];
        this.fetch();
        subscriber.unsubscribe();
      },
    );
    this.popupMessageService.messageSignal.emit(
      PopupMessages.DeleteRequestListMessage(
        this.requestService.deleteRequests(this.caches.map((r) => r.uuId)),
      ),
    );
  }

  cacheRequest(request: RequestListItem): void {
    if (request.selected) this.caches.push(request);
    else
      this.caches.splice(
        this.caches.findIndex((r) => r.uuId == request.uuId),
        1,
      );
  }

  applyAgain(request: RequestListItem): void {
    this.requestService.getRequestByUUId(request.uuId).subscribe((value) => {
      this.requestService.form.formGroup.reset();
      this.requestService.form.volunteers = [];
      this.requestService.form.requester = value.requester.uuId;
      this.requestService.form.formGroup.controls["service"].setValue(
        value.service.uuId,
      );
      this.requestService.form.formGroup.controls["start"].setValue(
        value.startTime,
      );
      this.requestService.form.formGroup.controls["end"].setValue(
        value.endTime,
      );
      this.requestService.form.formGroup.controls["venue"].setValue(
        value.venue.room.uuId,
      );
      this.requestService.form.formGroup.controls["destination"].setValue(
        value.destination.room.uuId,
      );
      this.requestService.form.formGroup.controls["numberOfVolunteer"].setValue(
        value.numberOfVolunteer,
      );
      this.requestService.form.formGroup.controls["remark"].setValue(
        value.remark,
      );
      this.requestService.form.formGroup.controls["transportation"].setValue(
        value.transportation ? value.transportation.uuId : null,
      );
      this.router.navigate(["/main/activity/create/request"]);
    });
  }

  selectAll(): void {
    if (this.isSelectedAll) {
      this.requests.forEach((r) => {
        r.selected = false;
        this.caches.splice(
          this.caches.findIndex((u) => u.uuId == r.uuId),
          1,
        );
      });
    } else {
      this.caches = this.caches.concat(
        this.requests.filter((request) => request.selected == false),
      );
      this.requests.forEach((r) => (r.selected = true));
    }
  }

  get isSelectedAll(): boolean {
    return this.requests.every((request) => request.selected == true);
  }

  private getQueryString(): string {
    return (
      "?start=" +
      (this.filters.page - 1) * this.filters.itemsPerPage +
      "&limit=" +
      this.filters.itemsPerPage +
      (this.filters.searchInput
        ? "&filterString=" + this.filters.searchInput
        : "") +
      this.filters.services
        .map((service: Service) => "&services=" + service.uuId)
        .join("") +
      this.filters.districts
        .map((district: string) => "&districts=" + district)
        .join("") +
      this.filters.subDistricts
        .map((subDistrict: string) => "&subDistricts=" + subDistrict)
        .join("") +
      this.filters.statuses.map((s: string) => "&statuses=" + s).join("") +
      (this.filters.from ? "&from=" + this.filters.from.toJSON() : "") +
      (this.filters.to ? "&to=" + this.filters.to.toJSON() : "") +
      (this.filters.idOrder != null
        ? "&orderById=" + String(this.filters.idOrder)
        : "") +
      (this.filters.dateOrder != null
        ? "&orderByDate=" + String(this.filters.dateOrder)
        : "") +
      (this.filters.isDeleted
        ? "&isDeleted=" + String(this.filters.isDeleted)
        : "") +
      "&isUrgent=true"
    );
  }
}
